import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';

const { Title, Text } = Typography;

const TaskManagerFunnelsPage = () => {
    const [loading, setLoading] = useState(false);
    const [funnels, setFunnels] = useState([]);
    const [projects, setProjects] = useState([]);
    const [roles, setRoles] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [workTypes, setWorkTypes] = useState([]);
    const [workTypeStagesMap, setWorkTypeStagesMap] = useState(
        {}
    );
    const [selectedId, setSelectedId] = useState(null);
    const [funnelOpen, setFunnelOpen] = useState(false);
    const [stageOpen, setStageOpen] = useState(false);
    const [routeOpen, setRouteOpen] = useState(false);
    const [editingFunnel, setEditingFunnel] = useState(null);
    const [editingStage, setEditingStage] = useState(null);
    const [editingRoute, setEditingRoute] = useState(null);
    const [funnelForm] = Form.useForm();
    const [stageForm] = Form.useForm();
    const [routeForm] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [funnelRes, metaRes, statusRes, accessRes] =
                await Promise.all([
                    tmApi.get('/api/task-manager/funnels'),
                    tmApi.get('/api/task-manager/meta'),
                    tmApi.get('/api/task-manager/statuses'),
                    tmApi.get('/api/task-manager/access-constructor')
                ]);
            const list = funnelRes.data.data || [];
            setFunnels(list);
            setRoles(metaRes.data?.roles || []);
            setStatuses(statusRes.data?.data || []);
            setProjects(accessRes.data?.data?.projects || []);
            setWorkTypes(accessRes.data?.data?.work_types || []);
            setWorkTypeStagesMap(
                accessRes.data?.data?.work_type_stages || {}
            );
            if (!selectedId && list.length) {
                setSelectedId(list[0].id);
            } else if (
                selectedId &&
                !list.find(f => f.id === selectedId)
            ) {
                setSelectedId(list[0]?.id || null);
            }
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки маршрутов'
            );
        } finally {
            setLoading(false);
        }
    }, [selectedId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = useMemo(
        () => funnels.find(f => f.id === selectedId) || null,
        [funnels, selectedId]
    );

    const workTypeSelectOptions = useMemo(() => {
        const taken = new Set();
        (funnels || []).forEach(f => {
            const wt = String(f.work_type || '').trim();
            if (
                wt &&
                (!editingFunnel ||
                    Number(f.id) !== Number(editingFunnel.id))
            ) {
                taken.add(wt);
            }
        });
        return (workTypes || [])
            .map(w => ({
                value: w.value,
                label: w.label || w.value
            }))
            .filter(w => w.value && !taken.has(String(w.value)));
    }, [funnels, workTypes, editingFunnel]);

    const reloadSelected = async id => {
        const fid = id || selectedId;
        if (!fid) {
            load();
            return;
        }
        try {
            const { data } = await tmApi.get(
                `/api/task-manager/funnels/${fid}`
            );
            setFunnels(prev =>
                prev.map(f =>
                    f.id === fid ? data.data : f
                )
            );
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось обновить маршрут'
            );
            load();
        }
    };

    const openCreateFunnel = () => {
        setEditingFunnel(null);
        funnelForm.resetFields();
        funnelForm.setFieldsValue({
            sort_order: 100,
            is_active: true
        });
        setFunnelOpen(true);
    };

    const openEditFunnel = record => {
        setEditingFunnel(record);
        funnelForm.setFieldsValue({
            ...record,
            project_id: record.project_id || undefined,
            role_id: record.role_id || undefined,
            work_type: record.work_type || undefined
        });
        setFunnelOpen(true);
    };

    const saveFunnel = async () => {
        try {
            const values = await funnelForm.validateFields();
            if (editingFunnel) {
                await tmApi.put(
                    `/api/task-manager/funnels/${editingFunnel.id}`,
                    values
                );
                message.success('Маршрут обновлён');
                setFunnelOpen(false);
                reloadSelected(editingFunnel.id);
            } else {
                const { data } = await tmApi.post(
                    '/api/task-manager/funnels',
                    values
                );
                message.success('Маршрут создан');
                setFunnelOpen(false);
                await load();
                setSelectedId(data.data?.id);
            }
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения маршрута'
            );
        }
    };

    const removeFunnel = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/funnels/${id}`
            );
            message.success('Маршрут удалён');
            setSelectedId(null);
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const openCreateStage = () => {
        setEditingStage(null);
        stageForm.resetFields();
        stageForm.setFieldsValue({
            sort_order: 100,
            color: '#2f6fed',
            is_initial: false,
            is_final: false
        });
        setStageOpen(true);
    };

    const openEditStage = record => {
        setEditingStage(record);
        stageForm.setFieldsValue(record);
        setStageOpen(true);
    };

    const saveStage = async () => {
        if (!selected) return;
        try {
            const values = await stageForm.validateFields();
            if (editingStage) {
                await tmApi.put(
                    `/api/task-manager/funnels/${selected.id}/stages/${editingStage.id}`,
                    values
                );
                message.success('Этап обновлён');
            } else {
                await tmApi.post(
                    `/api/task-manager/funnels/${selected.id}/stages`,
                    values
                );
                message.success('Этап добавлен');
            }
            setStageOpen(false);
            reloadSelected();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения этапа'
            );
        }
    };

    const removeStage = async id => {
        if (!selected) return;
        try {
            await tmApi.delete(
                `/api/task-manager/funnels/${selected.id}/stages/${id}`
            );
            message.success('Этап удалён');
            reloadSelected();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления этапа'
            );
        }
    };

    const openCreateRoute = () => {
        setEditingRoute(null);
        routeForm.resetFields();
        routeForm.setFieldsValue({
            sort_order: 100,
            is_active: true
        });
        setRouteOpen(true);
    };

    const openEditRoute = record => {
        setEditingRoute(record);
        routeForm.setFieldsValue({
            ...record
        });
        setRouteOpen(true);
    };

    const saveRoute = async () => {
        if (!selected) return;
        try {
            const values = await routeForm.validateFields();
            if (editingRoute) {
                await tmApi.put(
                    `/api/task-manager/funnels/${selected.id}/routes/${editingRoute.id}`,
                    values
                );
                message.success('Маршрут обновлён');
            } else {
                await tmApi.post(
                    `/api/task-manager/funnels/${selected.id}/routes`,
                    values
                );
                message.success('Маршрут добавлен');
            }
            setRouteOpen(false);
            reloadSelected();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения маршрута'
            );
        }
    };

    const removeRoute = async id => {
        if (!selected) return;
        try {
            await tmApi.delete(
                `/api/task-manager/funnels/${selected.id}/routes/${id}`
            );
            message.success('Маршрут удалён');
            reloadSelected();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления маршрута'
            );
        }
    };

    const stageName = id => {
        const s = (selected?.stages || []).find(
            x => x.id === id
        );
        return s?.human_name || s?.status_name || `#${id}`;
    };

    const roleName = id =>
        roles.find(r => Number(r.id) === Number(id))?.name ||
        `#${id}`;

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Маршруты этапов
                        </Title>
                        <Text type="secondary">
                            По роли и виду работ: этапы и маршруты
                            переходов (роль задаётся у маршрута)
                        </Text>
                    </div>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={load}
                        >
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreateFunnel}
                        >
                            Новый маршрут
                        </Button>
                    </Space>
                </Space>

                <Space align="start" wrap style={{ width: '100%' }}>
                    <Card
                        size="small"
                        title="Схемы"
                        style={{ width: 320 }}
                        loading={loading}
                    >
                        {(funnels || []).map(f => (
                            <div
                                key={f.id}
                                onClick={() =>
                                    setSelectedId(f.id)
                                }
                                style={{
                                    padding: '8px 10px',
                                    marginBottom: 6,
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    background:
                                        f.id === selectedId
                                            ? '#e6f4ff'
                                            : '#fafafa',
                                    border:
                                        f.id === selectedId
                                            ? '1px solid #91caff'
                                            : '1px solid #f0f0f0'
                                }}
                            >
                                <div>
                                    <Text strong>
                                        {f.human_name}
                                    </Text>
                                </div>
                                <Space size={4} wrap>
                                    {f.project?.human_name ? (
                                        <Tag>
                                            {
                                                f.project
                                                    .human_name
                                            }
                                        </Tag>
                                    ) : null}
                                    {f.work_type ? (
                                        <Tag color="blue">
                                            {f.work_type}
                                        </Tag>
                                    ) : (
                                        <Tag>все виды</Tag>
                                    )}
                                    {f.role_id ? (
                                        <Tag>
                                            {roleName(f.role_id)}
                                        </Tag>
                                    ) : (
                                        <Tag>все роли</Tag>
                                    )}
                                </Space>
                            </div>
                        ))}
                        {!funnels.length ? (
                            <Text type="secondary">
                                Воронок пока нет
                            </Text>
                        ) : null}
                    </Card>

                    <Card
                        size="small"
                        style={{ flex: 1, minWidth: 420 }}
                        title={
                            selected
                                ? selected.human_name
                                : 'Выберите маршрут'
                        }
                        extra={
                            selected ? (
                                <Space>
                                    <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                            openEditFunnel(
                                                selected
                                            )
                                        }
                                    >
                                        Изменить
                                    </Button>
                                    <Popconfirm
                                        title="Удалить маршрут?"
                                        onConfirm={() =>
                                            removeFunnel(
                                                selected.id
                                            )
                                        }
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            icon={
                                                <DeleteOutlined />
                                            }
                                        />
                                    </Popconfirm>
                                </Space>
                            ) : null
                        }
                    >
                        {selected ? (
                            <>
                                <Divider orientation="left">
                                    Этапы
                                </Divider>
                                <Button
                                    size="small"
                                    type="dashed"
                                    icon={<PlusOutlined />}
                                    onClick={openCreateStage}
                                    style={{ marginBottom: 8 }}
                                >
                                    Этап
                                </Button>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={false}
                                    dataSource={
                                        selected.stages || []
                                    }
                                    columns={[
                                        {
                                            title: 'Статус',
                                            dataIndex:
                                                'status_name'
                                        },
                                        {
                                            title: 'Название',
                                            dataIndex:
                                                'human_name'
                                        },
                                        {
                                            title: '',
                                            width: 100,
                                            render: (_, row) => (
                                                <Space>
                                                    <Button
                                                        size="small"
                                                        icon={
                                                            <EditOutlined />
                                                        }
                                                        onClick={() =>
                                                            openEditStage(
                                                                row
                                                            )
                                                        }
                                                    />
                                                    <Popconfirm
                                                        title="Удалить этап?"
                                                        onConfirm={() =>
                                                            removeStage(
                                                                row.id
                                                            )
                                                        }
                                                    >
                                                        <Button
                                                            size="small"
                                                            danger
                                                            icon={
                                                                <DeleteOutlined />
                                                            }
                                                        />
                                                    </Popconfirm>
                                                </Space>
                                            )
                                        }
                                    ]}
                                />

                                <Divider orientation="left">
                                    Переходы (из → в)
                                </Divider>
                                <Button
                                    size="small"
                                    type="dashed"
                                    icon={<PlusOutlined />}
                                    onClick={openCreateRoute}
                                    style={{ marginBottom: 8 }}
                                >
                                    Маршрут
                                </Button>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={false}
                                    dataSource={
                                        selected.routes || []
                                    }
                                    columns={[
                                        {
                                            title: 'Из',
                                            render: (_, r) =>
                                                stageName(
                                                    r.from_stage_id
                                                )
                                        },
                                        {
                                            title: 'В',
                                            render: (_, r) =>
                                                stageName(
                                                    r.to_stage_id
                                                )
                                        },
                                        {
                                            title: 'Название',
                                            dataIndex: 'name',
                                            render: v =>
                                                v || '—'
                                        },
                                        {
                                            title: '',
                                            width: 100,
                                            render: (_, row) => (
                                                <Space>
                                                    <Button
                                                        size="small"
                                                        icon={
                                                            <EditOutlined />
                                                        }
                                                        onClick={() =>
                                                            openEditRoute(
                                                                row
                                                            )
                                                        }
                                                    />
                                                    <Popconfirm
                                                        title="Удалить маршрут?"
                                                        onConfirm={() =>
                                                            removeRoute(
                                                                row.id
                                                            )
                                                        }
                                                    >
                                                        <Button
                                                            size="small"
                                                            danger
                                                            icon={
                                                                <DeleteOutlined />
                                                            }
                                                        />
                                                    </Popconfirm>
                                                </Space>
                                            )
                                        }
                                    ]}
                                />
                            </>
                        ) : (
                            <Text type="secondary">
                                Создайте маршрут слева
                            </Text>
                        )}
                    </Card>
                </Space>

                <Modal
                    open={funnelOpen}
                    title={
                        editingFunnel
                            ? `Маршрут #${editingFunnel.id}`
                            : 'Новый маршрут'
                    }
                    onCancel={() => setFunnelOpen(false)}
                    onOk={saveFunnel}
                    destroyOnClose
                >
                    <Form form={funnelForm} layout="vertical">
                        <Form.Item
                            name="human_name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="project_id"
                            label="Проект"
                        >
                            <Select
                                allowClear
                                options={projects.map(p => ({
                                    value: p.id,
                                    label: p.human_name
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="role_id"
                            label="Роль (пусто = все)"
                        >
                            <Select
                                allowClear
                                options={roles.map(r => ({
                                    value: r.id,
                                    label: r.name
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="work_type"
                            label="Вид работ"
                            extra="На один вид работ — только один маршрут"
                        >
                            <Select
                                allowClear
                                options={workTypeSelectOptions}
                                notFoundContent="Все виды работ уже заняты маршрутами"
                            />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Описание"
                        >
                            <Input.TextArea rows={2} />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активна"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={stageOpen}
                    title={
                        editingStage
                            ? `Этап #${editingStage.id}`
                            : 'Новый этап'
                    }
                    onCancel={() => setStageOpen(false)}
                    onOk={saveStage}
                    destroyOnClose
                >
                    <Form form={stageForm} layout="vertical">
                        <Form.Item
                            name="status_name"
                            label="Статус заявки"
                            rules={[{ required: true }]}
                            extra={
                                selected?.work_type
                                    ? 'Сначала этапы вида работ, затем общие статусы'
                                    : undefined
                            }
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                options={[
                                    ...((selected?.work_type &&
                                        (
                                            workTypeStagesMap[
                                                selected
                                                    .work_type
                                            ] || []
                                        ).map(s => ({
                                            value: s.status_name,
                                            label: `${s.human_name || s.status_name} (${selected.work_type})`
                                        }))) ||
                                        []),
                                    ...statuses.map(s => ({
                                        value: s.name,
                                        label: s.name
                                    }))
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="human_name"
                            label="Отображаемое имя"
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item name="color" label="Цвет">
                            <Input type="color" />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_initial"
                            label="Начальный"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="is_final"
                            label="Финальный"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={routeOpen}
                    title={
                        editingRoute
                            ? `Маршрут #${editingRoute.id}`
                            : 'Новый маршрут'
                    }
                    onCancel={() => setRouteOpen(false)}
                    onOk={saveRoute}
                    destroyOnClose
                >
                    <Form form={routeForm} layout="vertical">
                        <Form.Item
                            name="from_stage_id"
                            label="Из этапа"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={(
                                    selected?.stages || []
                                ).map(s => ({
                                    value: s.id,
                                    label:
                                        s.human_name ||
                                        s.status_name
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="to_stage_id"
                            label="В этап"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={(
                                    selected?.stages || []
                                ).map(s => ({
                                    value: s.id,
                                    label:
                                        s.human_name ||
                                        s.status_name
                                }))}
                            />
                        </Form.Item>
                        <Form.Item name="name" label="Название">
                            <Input placeholder="Необязательно" />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активен"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerFunnelsPage;
